import {
  GHG_ESTIMATOR_CONCEPT_ID,
  IMPACT_CARRY_CONCEPT_ID,
  IMPACT_STRATEGY_CONCEPT_ID,
  impactIconForConceptId,
} from '~/constants/concepts'
import { arrayAverage } from '~/functions/helpers'
import { isImpactLocked } from '~/functions/impact-locking'
import type { ImpactGoalType, ImpactSolutionType, ImpactType } from '~/types/types'

export type ImpactFlowSection = {
  label: string
  id: string
  percentageCompleted?: number
  prepend?: boolean
  icon?: string
  locked?: boolean
  sections?: ImpactFlowSection[]
}

const isAnswered = (val: unknown) => {
  if (val === undefined || val === null || val === '') return 0
  if (Array.isArray(val) && val.length === 0) return 0
  return 1
}

export const completionDefine = (impact: ImpactType) =>
  arrayAverage(
    isAnswered(impact.problem),
    isAnswered(impact.conceptId),
    isAnswered(impact.stakeholder),
  )

export const completionClassify = (impact: ImpactType) =>
  arrayAverage(isAnswered(impact.sdgTargets))

export const completionSingleImpactSolution = (solution: ImpactSolutionType) =>
  arrayAverage(
    isAnswered(solution.kind),
    isAnswered(solution.concept?.label),
    isAnswered(solution.targetGroup),
    isAnswered(solution.solutionDescription),
  )

export const completionSolutions = (impact: ImpactType) => {
  if (!isAnswered(impact.impactSolutions)) return 0
  return arrayAverage(
    ...(impact.impactSolutions ?? []).map((solution) =>
      completionSingleImpactSolution(solution),
    ),
  )
}

export const completionMeasure = (impact: ImpactType) =>
  isAnswered(impact.metricAssignments)

export const completionSingleImpact = (impact: ImpactType) =>
  arrayAverage(
    completionDefine(impact),
    completionClassify(impact),
    completionSolutions(impact),
    completionMeasure(impact),
  )

export function getBasicImpactSections(
  impact: ImpactType,
  options: {
    ventureId: string
    doGhgCheck: boolean
    t: (key: string) => string
  },
): ImpactFlowSection[] {
  const { ventureId, doGhgCheck, t } = options

  if (Number(impact.conceptId) === IMPACT_CARRY_CONCEPT_ID) {
    return [
      {
        label: t('impactGoal.navigation.impactCarry.label'),
        id: `/${ventureId}/impact/edit/impacts/${impact.id}/impactCarry`,
        sections: [],
      },
    ]
  }

  return [
    {
      label: t('impactGoal.navigation.problemDescription.label'),
      id: `/${ventureId}/impact/edit/impacts/${impact.id}/define`,
      percentageCompleted: completionDefine(impact),
      sections: [],
    },
    {
      label: t('impactGoal.navigation.internationalFrameworks.label'),
      id: `/${ventureId}/impact/edit/impacts/${impact.id}/classify`,
      percentageCompleted: completionClassify(impact),
      sections: [],
    },
    {
      label: t('impactGoal.navigation.impactSolutions.label'),
      id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/`,
      percentageCompleted: completionSolutions(impact),
      sections: (impact.impactSolutions ?? []).map((solution, solutionIndex) => ({
        label: solution.concept?.label
          ? solution.concept.label
          : t('impactGoal.navigation.impactSolutionNumber.label') + (solutionIndex + 1),
        id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/${solutionIndex}`,
        percentageCompleted: completionSingleImpactSolution(solution),
        sections:
          doGhgCheck &&
          impact.hasFrozenOutcome &&
          impact.conceptId === GHG_ESTIMATOR_CONCEPT_ID &&
          solution.useGhgSavingPotentialTool
            ? [
                {
                  label: t('impactGoal.navigation.ghgSavingPotential.label'),
                  id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/${solutionIndex}/ghgSavingPotential`,
                  sections: [
                    {
                      label: t('impactGoal.navigation.impactProfile.label'),
                      id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/${solutionIndex}/ghgSavingPotential/impactProfile`,
                    },
                    {
                      label: t('impactGoal.navigation.fairComparison.label'),
                      id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/${solutionIndex}/ghgSavingPotential/fairComparison`,
                    },
                    {
                      label: t('impactGoal.navigation.emissionSources.label'),
                      id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/${solutionIndex}/ghgSavingPotential/emissionSources`,
                    },
                    {
                      label:
                        impact.conceptId !== GHG_ESTIMATOR_CONCEPT_ID
                          ? t('impactGoal.navigation.growthFactor.label')
                          : t('impactGoal.navigation.growthFactorReducedCo2.label'),
                      id: `/${ventureId}/impact/edit/impacts/${impact.id}/solutions/${solutionIndex}/ghgSavingPotential/growthFactor`,
                    },
                  ],
                },
              ]
            : [],
      })),
    },
  ]
}

export function buildImpactStrategySections(
  tocImpact: ImpactType,
  options: {
    ventureId: string
    t: (key: string) => string
    requireImpactSubmission?: boolean
  },
): ImpactFlowSection[] {
  const { ventureId, t, requireImpactSubmission = true } = options
  return [
    {
      label: tocImpact.concept?.label
        ? tocImpact.concept.label
        : t('impactGoal.navigation.impactStrategy.label'),
      id: `/${ventureId}/impact/edit/impacts/${tocImpact.id}`,
      percentageCompleted: 1,
      prepend: true,
      icon: 'mdi-strategy',
      locked: isImpactLocked(tocImpact),
      sections: [
        {
          label: t('impactGoal.navigation.impactStrategy.label'),
          id: `/${ventureId}/impact/edit/impacts/${tocImpact.id}/impactStrategy`,
          sections: [],
        },
        {
          label: t('impactGoal.navigation.theoryofChange.label'),
          id: `/${ventureId}/impact/edit/impacts/${tocImpact.id}/theoryOfChange`,
          sections: [],
        },
        {
          label: requireImpactSubmission
            ? t('impactGoal.navigation.nextSteps.label')
            : t('impactGoal.navigation.done.label'),
          id: `/${ventureId}/impact/edit/impacts/${tocImpact.id}/submit`,
          sections: [],
        },
      ],
    },
  ]
}

export function buildImpactSections(
  impactGoal: Partial<ImpactGoalType>,
  options: {
    ventureId: string
    doGhgCheck: boolean
    t: (key: string) => string
    requireImpactSubmission?: boolean
  },
): ImpactFlowSection[] {
  const { ventureId, doGhgCheck, t, requireImpactSubmission = true } = options
  if (!impactGoal?.impacts) return []

  return impactGoal.impacts
    .filter(
      (impact: ImpactType) =>
        Number(impact.conceptId) !== IMPACT_STRATEGY_CONCEPT_ID,
    )
    .map((impact: ImpactType, index) => ({
      label: impact.concept?.label
        ? impact.concept.label
        : t('impactGoal.navigation.impactNumber.label') + (index + 1),
      id: `/${ventureId}/impact/edit/impacts/${impact.id}`,
      percentageCompleted: 1,
      prepend: true,
      icon: impactIconForConceptId(impact.conceptId),
      locked: isImpactLocked(impact),
      sections: [
        ...getBasicImpactSections(impact, { ventureId, doGhgCheck, t }),
        ...(impact.conceptId !== GHG_ESTIMATOR_CONCEPT_ID ||
        impact.conceptId === IMPACT_STRATEGY_CONCEPT_ID ||
        impact.conceptId === IMPACT_CARRY_CONCEPT_ID
          ? [
              {
                label: t('impactGoal.navigation.impactMeasurement.label'),
                id: `/${ventureId}/impact/edit/impacts/${impact.id}/measure`,
                percentageCompleted: completionMeasure(impact),
                sections: [],
              },
            ]
          : []),
        {
          label: requireImpactSubmission
            ? t('impactGoal.navigation.nextSteps.label')
            : t('impactGoal.navigation.done.label'),
          id: `/${ventureId}/impact/edit/impacts/${impact.id}/submit`,
          sections: [],
        },
      ],
    }))
}

export function buildImpactEditSections(options: {
  ventureId: string
  impactGoal: Partial<ImpactGoalType>
  hasImpactStrategyModule: boolean
  doGhgCheck: boolean
  t: (key: string) => string
  requireImpactSubmission?: boolean
}): ImpactFlowSection[] {
  const {
    ventureId,
    impactGoal,
    hasImpactStrategyModule,
    doGhgCheck,
    t,
    requireImpactSubmission = true,
  } = options

  const tocImpact = impactGoal?.impacts?.find(
    (impact: ImpactType) =>
      Number(impact.conceptId) === IMPACT_STRATEGY_CONCEPT_ID,
  )

  const impactSections = buildImpactSections(impactGoal, {
    ventureId,
    doGhgCheck,
    t,
    requireImpactSubmission,
  })

  const areAllImpactSectionsLocked =
    impactSections.length > 0 && impactSections.every((section) => section.locked)

  const completionImpacts = () => {
    if (!isAnswered(impactGoal?.impacts)) return 0
    return arrayAverage(
      ...(impactGoal.impacts
        ?.filter(
          (impact: ImpactType) =>
            Number(impact.conceptId) !== IMPACT_STRATEGY_CONCEPT_ID,
        )
        ?.map((impact: ImpactType) => completionSingleImpact(impact)) ?? []),
    )
  }

  return [
    {
      label: t('impactGoal.navigation.introduction.label'),
      id: `/${ventureId}/impact/edit/intro`,
    },
    ...(hasImpactStrategyModule
      ? [
          {
            label: t('impactGoal.navigation.myImpactStrategy.label'),
            id: `/${ventureId}/impact/edit/myImpactStrategy`,
            sections: tocImpact
              ? buildImpactStrategySections(tocImpact, {
                  ventureId,
                  t,
                  requireImpactSubmission,
                })
              : [],
            locked: tocImpact ? isImpactLocked(tocImpact) : false,
          },
        ]
      : []),
    {
      label: t('impactGoal.navigation.impactDefinition.label'),
      id: `/${ventureId}/impact/edit/impacts`,
      percentageCompleted: completionImpacts(),
      locked: areAllImpactSectionsLocked,
      sections: impactSections,
    },
  ]
}
